import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { isEqual } from 'lodash'
import { getParsedItem } from '@sketch/utils'

const SORT_OPTIONS = ['name', 'created-at', 'last-modified'] as const
const SORT_ORDERS = ['asc', 'desc'] as const
const DEFAULT_SORT_BY = 'last-modified' as SortBy
const DEFAULT_SORT_ORDER = 'desc' as SortOrder

export type SortBy = (typeof SORT_OPTIONS)[number]
export type SortOrder = (typeof SORT_ORDERS)[number]

export type ShareSortOrder =
  | 'CREATED_AT_ASC'
  | 'CREATED_AT_DESC'
  | 'LAST_MODIFIED_ASC'
  | 'LAST_MODIFIED_DESC'
  | 'NAME_ASC'
  | 'NAME_DESC'

const MAP_SORT_ORDER: Record<SortBy, Record<SortOrder, ShareSortOrder>> = {
  'created-at': {
    asc: 'CREATED_AT_ASC',
    desc: 'CREATED_AT_DESC',
  },
  'last-modified': {
    asc: 'LAST_MODIFIED_ASC',
    desc: 'LAST_MODIFIED_DESC',
  },
  name: {
    asc: 'NAME_ASC',
    desc: 'NAME_DESC',
  },
}

const MAP_SORTING_LABEL: Record<SortBy, Record<SortOrder, string>> = {
  'created-at': {
    asc: 'Date created / Older first',
    desc: 'Date created / Newer first',
  },
  'last-modified': {
    asc: 'Last updated / Older first',
    desc: 'Last updated / Newer first',
  },
  name: {
    asc: 'Name / A-Z',
    desc: 'Name / Z-A',
  },
}

const useGetSynchedParams = <T extends string>(
  paramName: string,
  defaultValue: T,
  validValues?: T[]
): T => {
  const storageKey = `query-${paramName}`

  const { search } = useLocation()
  const [storageValue, setStorageValue] = useState(
    getParsedItem(storageKey) as T | null
  )

  const searchParams = useMemo(() => new URLSearchParams(search), [search])
  const paramsValue = searchParams.get(paramName) as T | null

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) {
        setStorageValue(JSON.parse(event.newValue as T))
      }
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [storageKey])

  const rawValues = paramsValue || storageValue

  if (validValues) {
    return validValues.find(value => rawValues === value) || defaultValue
  }

  return paramsValue || storageValue || defaultValue
}

export const useSortingSettings = () => {
  const sortBy = useGetSynchedParams('sort-by', DEFAULT_SORT_BY, [
    ...SORT_OPTIONS,
  ])
  const sortOrder = useGetSynchedParams('sort-order', DEFAULT_SORT_ORDER, [
    ...SORT_ORDERS,
  ])

  return {
    sortBy,
    sortOrder,
    sort: MAP_SORT_ORDER[sortBy][sortOrder],
    sortLabel: MAP_SORTING_LABEL[sortBy][sortOrder],
  }
}

interface SetSortProps {
  sortBy?: string
  sortOrder?: string
  sync?: boolean
}

const setSortSettingsStorage = (storageKey: string, value: string | null) => {
  const stringifiedValue = JSON.stringify(value)

  if (value === null) {
    localStorage.removeItem(storageKey)
  } else {
    localStorage.setItem(storageKey, stringifiedValue)
  }

  const storageEvent = new StorageEvent('storage', {
    key: storageKey,
    storageArea: localStorage,
    newValue: value === null ? null : stringifiedValue,
    oldValue: '', // We don't need this oldvalue to simulate the storage update
  })

  window.dispatchEvent(storageEvent)
}

export const useSetSortingSettings = () => {
  const { sortBy, sortOrder } = useSortingSettings()

  const sorting = {
    'sort-by': sortBy,
    'sort-order': sortOrder,
  }

  const cacheSorting = useRef(sorting)
  cacheSorting.current = sorting

  const history = useHistory()

  const setSort = useCallback(
    ({ sortBy, sortOrder, sync }: SetSortProps) => {
      const queryParams = {
        'sort-by': sortBy || cacheSorting.current['sort-by'],
        'sort-order': sortOrder || cacheSorting.current['sort-order'],
      }

      if (!sync && isEqual(queryParams, cacheSorting.current)) {
        return
      }

      const newURL = new URLSearchParams(queryParams)

      if (queryParams['sort-by'] === DEFAULT_SORT_BY) {
        newURL.delete('sort-by')
        setSortSettingsStorage('query-sort-by', null)
      } else {
        setSortSettingsStorage('query-sort-by', queryParams['sort-by'])
      }

      if (queryParams['sort-order'] === DEFAULT_SORT_ORDER) {
        newURL.delete('sort-order')
        setSortSettingsStorage('query-sort-order', null)
      } else {
        setSortSettingsStorage('query-sort-order', queryParams['sort-order'])
      }

      history.replace({ search: newURL.toString() })
    },
    [history]
  )

  return setSort
}

export const useSyncSortSettings = () => {
  const location = useLocation()

  const setSort = useSetSortingSettings()

  const cachedSetSort = useRef(setSort)
  cachedSetSort.current = setSort

  useEffect(() => {
    cachedSetSort.current({ sync: true })

    // Listen to the pathname changes to set the sort settings query params
    // every time the user navigates to a different path
  }, [location.pathname])
}
